import { AfterViewInit, Component, ElementRef, HostListener, Inject, inject, OnInit, ViewChild, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { ThemeService } from './services/theme.service';
import { SessionService } from './services/session.service';
import { environment } from 'src/environments/environment';
import { MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import { CommonService } from './services/common.service';
import { TranslateService } from '@ngx-translate/core';
import { TourService } from 'ngx-ui-tour-md-menu';

import { SessionMonitorService } from './services/session-monitor.service';
import { HealthStateService } from './services/health-state.service';
import { SetupStateService } from './services/setup-state.service';
import { GuideService } from './services/guide.service';
import { AppUpdateService } from './services/app-update.service';
import { AppChromeService } from './services/app-chrome.service';

@Component({
    selector: 'loyale-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {
  private readonly mobileBreakpoint = 600;
  private readonly compactNavigationBreakpoint = 1100;

  mobileMode = false;
  navigationOverlayMode = false;
  sidenavMode = 'side';
  sidenavOpen = true;
  releaseNotesSidenavOpen = false;

  env = environment;

  superClickCounter = 0;

  private readonly tourService = inject(TourService);
  readonly appChrome = inject(AppChromeService);

  @ViewChild('appSidenav') sidenav: MatSidenav;
  @ViewChild('navigationContainer') navigationContainer: MatSidenavContainer;

  // Keep enough room for data-heavy pages by moving the navigation into an
  // overlay before the desktop sidebar begins to compress their content.
  @HostListener('window:resize', ['$event']) onResize(event) {
    this.setLayout(event.target.innerWidth);
  }

  constructor(
    public sessionService: SessionService,
    private translateService: TranslateService,
    public commonService: CommonService,
    public themeService: ThemeService,
    @Inject(DOCUMENT) private document: Document,
    private sessionMonitorService: SessionMonitorService,
    private healthStateService: HealthStateService,
    private setupStateService: SetupStateService,
    private guideService: GuideService,
    private appUpdateService: AppUpdateService,
  ) { }

  ngOnInit() {
    console.info(this.env.buildInfo.version);
    this.appUpdateService.startMonitoring();

    if (this.requiresV3UpdateSignOut()) {
      localStorage.clear();
      sessionStorage.clear();
      this.commonService.setCookie('loyale_session_reset_under_version', '3.0.0', 365);
      this.sessionService.signOut();
      return;
    }

    const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    const environmentName = isLocalhost ? 'Localhost' : environment.name;

    this.document.title = environment.production && !isLocalhost
      ? environment.projectName
      : `${environment.projectName} - ${environmentName}`;

    //set translation to browser navigator language
    this.translateService.setDefaultLang('en');
    this.translateService.use(navigator.language);

    //set theme
    this.themeService.theme = 'purple-orange-theme';

    // Clear deprecated guide-service browser state on every app startup.
    this.guideService.clearDeprecatedStorage();

    this.setLayout(window.innerWidth);

    this.sessionMonitorService.startMonitoring();

    // Otherwise /load starts these, once the scheme they should poll is settled.
    if (this.sessionService.tokenValid() && this.sessionService.schemeId && this.sessionService.isSessionReady) {
      this.healthStateService.startBackgroundSync();
      this.setupStateService.startBackgroundSync();
    }
  }

  setLayout(windowWidth: number) {
    this.mobileMode = windowWidth <= this.mobileBreakpoint;
    this.navigationOverlayMode = windowWidth <= this.compactNavigationBreakpoint;

    if (this.navigationOverlayMode) {
      this.sidenavMode = 'over';
      this.sidenavOpen = false;
    } else {
      this.sidenavMode = 'side';
      this.sidenavOpen = true;
    }
  }

  onNavigationWidthChange(): void {
    // The collapsed class is rendered after the child output fires. Recalculate
    // once on the next frame so Material measures the final drawer width rather
    // than continuously measuring on every change-detection pass via autosize.
    requestAnimationFrame(() => this.navigationContainer?.updateContentMargins());
  }

  onSuperClick() {
    if (this.superClickCounter == 4) {
      this.sessionService.redirectToAuth('super');
    } else {
      this.superClickCounter = this.superClickCounter + 1;
    }
  }

  toggleReleaseNotesSidenav(): void {
    this.releaseNotesSidenavOpen = !this.releaseNotesSidenavOpen;
  }

  closeReleaseNotesSidenav(): void {
    this.releaseNotesSidenavOpen = false;
  }

  private requiresV3UpdateSignOut(): boolean {
    return this.env.production &&
      this.isVersionBefore(this.env.buildInfo.version, [3, 0, 0]) &&
      this.commonService.getCookie('loyale_session_reset_under_version') !== '3.0.0';
  }

  private isVersionBefore(version: string, target: [number, number, number]): boolean {
    const match = /^(\d+)\.(\d+)\.(\d+)/.exec(version);
    if (!match) {
      return false;
    }

    const parts = [Number(match[1]), Number(match[2]), Number(match[3])];
    for (let index = 0; index < target.length; index++) {
      if (parts[index] !== target[index]) {
        return parts[index] < target[index];
      }
    }

    return false;
  }

}
